import React from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/utils/cn';

const PAGE_SIZE_OPTIONS = [6, 9, 12, 24];

interface LibraryPaginationProps {
    pageIndex: number;
    pageSize: number;
    totalItems: number;
    onPageIndexChange: (pageIndex: number) => void;
    onPageSizeChange: (pageSize: number) => void;
}

export const LibraryPagination: React.FC<LibraryPaginationProps> = ({
    pageIndex,
    pageSize,
    totalItems,
    onPageIndexChange,
    onPageSizeChange,
}) => {
    if (totalItems === 0) {
        return null;
    }

    const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
    const start = pageIndex * pageSize + 1;
    const end = Math.min(totalItems, (pageIndex + 1) * pageSize);

    return (
        <div className="text-cocoa-500 flex shrink-0 items-center gap-4 text-sm">
            <span>
                {start}–{end} of {totalItems}
            </span>
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    onClick={() => onPageIndexChange(Math.max(0, pageIndex - 1))}
                    disabled={pageIndex === 0}
                    className="border-cocoa-100 hover:bg-cocoa-50 flex h-7 w-7 items-center justify-center rounded-md border disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Previous page"
                >
                    <LuChevronLeft className="h-4 w-4" />
                </button>
                {Array.from({ length: pageCount }, (_, page) => page).map((page) => (
                    <button
                        key={page}
                        type="button"
                        onClick={() => onPageIndexChange(page)}
                        className={cn(
                            'flex h-7 w-7 items-center justify-center rounded-md text-sm font-medium',
                            page === pageIndex ? 'bg-cocoa-900 text-white' : 'hover:bg-cocoa-50',
                        )}
                    >
                        {page + 1}
                    </button>
                ))}
                <button
                    type="button"
                    onClick={() => onPageIndexChange(Math.min(pageCount - 1, pageIndex + 1))}
                    disabled={pageIndex >= pageCount - 1}
                    className="border-cocoa-100 hover:bg-cocoa-50 flex h-7 w-7 items-center justify-center rounded-md border disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Next page"
                >
                    <LuChevronRight className="h-4 w-4" />
                </button>
            </div>
            <div className="flex items-center gap-2">
                <span>Per page</span>
                <Select value={pageSize.toString()} onValueChange={(value) => onPageSizeChange(Number(value))}>
                    <SelectTrigger className="h-8 w-[70px] bg-white">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {PAGE_SIZE_OPTIONS.map((size) => (
                            <SelectItem key={size} value={size.toString()}>
                                {size}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        </div>
    );
};
